/* eslint-disable */
import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import s from './cards.module.css';
import { Ico } from '../ShellIcons';
import { useShellStore } from '../shellStore';
import { DevCompareModal } from '../../../DevCompareModal';

// ============================================================
// BasedOnPeek — ONE fixed layer for the whole board (mounted once by the
// shell), anchored to whichever based-on chip is hovered or pinned. It reads
// shellStore.peek = { dev, model, anchorRect, onOpenDev, catalogItems, allDevs }
// and renders above the chip when there is room under the chrome, else below.
// Portaled to document.body (Convention #142: it declares its own tokens).
// ============================================================

const PLACEHOLDER = (
  <svg viewBox="0 0 140 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <ellipse cx="44" cy="32" rx="30" ry="21" /><ellipse cx="96" cy="32" rx="30" ry="21" /><path d="M74 32h-8M14 27 2 22M126 27l12-5" />
  </svg>
);
const W = 272;

export default function BasedOnPeek() {
  const peek = useShellStore((st) => st.peek);
  const pinned = useShellStore((st) => st.peekPinned);
  const clearPeek = useShellStore((st) => st.clearPeek);
  const chromeH = useShellStore((st) => st.appBarH + st.filterBarH + st.railH);
  const ref = useRef(null);
  const timer = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0, below: false, ax: W / 2 });
  const [compare, setCompare] = useState(false);

  useLayoutEffect(() => {
    const r = peek && peek.anchorRect; const el = ref.current;
    if (!r || !el) return;
    const H = el.offsetHeight || 300;
    const x = Math.min(window.innerWidth - W - 12, Math.max(12, r.left + r.width / 2 - W / 2));
    const above = r.top - H - 12 > chromeH;
    const y = above ? r.top - H - 10 : r.bottom + 10;
    setPos({ x, y, below: !above, ax: r.left + r.width / 2 - x });
  }, [peek, chromeH]);

  if (!peek || !peek.model || !peek.model.basedOn) return null;
  const b = peek.model.basedOn; const m = peek.model; const dev = peek.dev;
  const keep = () => clearTimeout(timer.current);
  const leave = () => { if (!pinned) timer.current = setTimeout(() => clearPeek(false), 200); };
  const openDev = () => { clearPeek(true); if (b.kind === 'dev' && peek.onOpenDev) peek.onOpenDev(b.dev); };
  const openItem = () => {
    clearPeek(true);
    if (b.itemId) { try { window.dispatchEvent(new CustomEvent('open_catalog_item', { detail: { id: b.itemId } })); } catch (e) { /* no window */ } }
  };
  const img = b.image ? <img loading="lazy" src={b.image} alt="" /> : <span className={s.ph}>{PLACEHOLDER}</span>;

  return ReactDOM.createPortal(
    <>
      <div ref={ref} className={`${s.peek} ${s.open} ${pos.below ? s.below : ''}`} style={{ left: pos.x, top: pos.y, '--ax': `${pos.ax}px` }}
        data-shell-pop role="dialog" aria-label="Based on" onMouseEnter={keep} onMouseLeave={leave} onClick={(e) => e.stopPropagation()}>
        <span className={s.pkArrow} />
        {b.kind === 'dev' ? (
          <>
            <div className={s.pkImg} style={{ '--stage': b.stage.c, '--stageInk': b.stage.ink, '--stageSoft': b.stage.soft }}>
              {img}<span className={s.pkTag}><span className={s.stageTag}><i /><span>{b.stage.name}</span></span></span>
            </div>
            <div className={s.pkBody}>
              <div className={s.pkT}><b>{b.no}</b>{b.og ? <span className={s.og}>OG</span> : null}<span className={s.pkSub}>development · by {b.designer}</span></div>
              <div className={s.pkChips}>{b.shape ? <span className={`${s.fchip} ${s.amber}`}>{b.shape}</span> : null}{m.material ? <span className={s.fchip}>{m.material}</span> : null}</div>
              <div className={s.pkLine}><Ico name="link" /><span><b>{m.no}</b> is a variant of this development</span></div>
              <div className={s.pkAct}>
                <button type="button" className={`${s.btn} ${s.primary}`} onClick={openDev}><Ico name="arrow" /><span>Open {b.no}</span></button>
                <button type="button" className={s.btn} onClick={() => setCompare(true)}><Ico name="compare" /><span>Compare</span></button>
              </div>
            </div>
          </>
        ) : b.kind === 'catalog' ? (
          <>
            <div className={s.pkImg}>{img}{b.sun != null ? <span className={s.pkTag}><span className={s.tag}>{b.sun ? 'Sun' : 'Optic'}</span></span> : null}</div>
            <div className={s.pkBody}>
              <div className={s.pkT}><b>{b.brand}</b>{b.sku ? <span className={s.pkSku}>{b.sku}</span> : null}</div>
              <div className={s.pkChips}>{b.shape ? <span className={`${s.fchip} ${s.amber}`}>{b.shape}</span> : null}{b.gender ? <span className={s.fchip}>{b.gender}</span> : null}{b.material ? <span className={s.fchip}>{b.material}</span> : null}</div>
              <div className={s.pkLine}><Ico name="back" /><span>{b.devsCount ? <><b>{b.devsCount}</b> development{b.devsCount > 1 ? 's' : ''} based on this original</> : <><b>{m.no}</b> is based on this original</>}</span></div>
              <div className={s.pkAct}>
                {b.itemId ? <button type="button" className={`${s.btn} ${s.primary}`} onClick={openItem}><Ico name="arrow" /><span>Open in Originals</span></button> : null}
                <button type="button" className={s.btn} onClick={() => setCompare(true)}><Ico name="compare" /><span>Compare</span></button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={s.pkImg}>{img}<span className={s.pkTag}><span className={s.tag}>{b.label}</span></span></div>
            <div className={s.pkBody}>
              <div className={s.pkT}><b>{b.name}</b>{b.ref ? <span className={s.pkSku}>{b.ref}</span> : null}</div>
              <div className={s.pkLine}><Ico name="back" /><span><b>{m.no}</b> was designed from this {b.label}</span></div>
              <div className={s.pkAct}><button type="button" className={`${s.btn} ${s.primary}`} onClick={() => setCompare(true)}><Ico name="compare" /><span>Compare side by side</span></button></div>
            </div>
          </>
        )}
      </div>
      {compare ? <DevCompareModal dev={dev} catalogItems={peek.catalogItems || []} allDevs={peek.allDevs || []} onClose={() => { setCompare(false); clearPeek(true); }} /> : null}
    </>,
    document.body
  );
}
